import React, { createContext, useContext, useMemo } from "react";
import { useNotifications } from "../hooks/notifications/useNotifications";
import type {
  AppNotification,
  NotificationCategory,
} from "../models/Notification/notificationTypes";

type NotificationContextValue = {
  notifications: AppNotification[];
  welcomeNotificationsForPopup: AppNotification[];
  isLoading: boolean;
  error: Error | null;
  reload: () => void;
};

const NotificationContext = createContext<NotificationContextValue | null>(
  null,
);

type NotificationProviderProps = {
  children: React.ReactNode;
  category?: NotificationCategory;
};

export const NotificationProvider = ({
  children,
  category = "Notification",
}: NotificationProviderProps) => {
  const {
    data: notifications,
    popupNotifications,
    isLoading,
    error,
    reload,
  } = useNotifications({ mode: "user", category });

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      welcomeNotificationsForPopup: popupNotifications,
      isLoading,
      error,
      reload,
    }),
    [notifications, popupNotifications, isLoading, error, reload],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider.",
    );
  }
  return ctx;
};
